// AlignmentPlayerView.tsx
// What a guild member who is not an officer sees on the Alignment tab: their
// own answers, and how the guild is leaning on side and realm type.
//
// Deliberately nothing about anybody else. The faction and realm figures are
// bare counts, which is the entire reason they are safe to show; no other
// player's name, note or class list appears here.
import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "./config";
import { ClassCoverage, ROLE_COLOR, RoleCoverage, TugOfWar, tc, useAlignmentTheme } from "./alignmentUi";
import { buildClasses, buildRoles, PoolStats } from "./alignmentStats";

type CatalogItem = {
  item_key: string; label: string; parent_key: string | null;
  role_key: string | null; color: string | null;
  metadata: { is_secondary?: boolean } | null;
};
type Catalog = {
  classes: CatalogItem[]; specs: CatalogItem[]; roles: CatalogItem[];
  professions: CatalogItem[]; factions: CatalogItem[]; realmTypes: CatalogItem[];
};
type Combo = {
  id: string; class_key: string; spec_key: string;
  professions: string[]; rank: number; confirmed: boolean;
};
type Board = {
  poolName: string; guildName: string; shareToken: string | null;
  heroImageUrl: string | null; catalog: Catalog; registered: number;
  me: {
    display_name: string; note: string | null; faction: string | null;
    realm_type: string | null; open_to_anything: boolean; status: string;
    combos: Combo[];
  } | null;
  factions: Record<string, number>;
  realmTypes: Record<string, number>;
  // Counts only, from the same helper the officer page and the sign up form
  // read. Nothing in here names anybody, which is what makes it safe on a
  // page every member of the guild can open.
  stats?: PoolStats;
};


export default function AlignmentPlayerView() {
  useAlignmentTheme();
  const [boards, setBoards] = useState<Board[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/me/alignment`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not load your sign up.");
      const data = await res.json();
      setBoards(data.boards || []);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-700 dark:text-rose-300">{error}</div>;
  }
  if (!boards) {
    return <div className="p-8 text-center text-sm text-[var(--c-ink-4)]">Loading.</div>;
  }
  if (boards.length === 0) {
    return (
      <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)] p-10 text-center">
        <div className="text-lg font-bold text-[var(--c-ink)]">Nothing to sign up for yet</div>
        <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--c-ink-3)]">
          When your officers open a WoW Forever sign up, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {boards.map((b) => {
        const label = (kind: "factions" | "realmTypes", key: string) =>
          (kind === "factions" ? b.catalog.factions : b.catalog.realmTypes)
            .find((x) => x.item_key === key)?.label || key;
        const specOf = (k: string) => b.catalog.specs.find((s) => s.item_key === k);
        const classOf = (k: string) => b.catalog.classes.find((c) => c.item_key === k);
        const profLabel = (k: string) =>
          b.catalog.professions.find((p) => p.item_key === k)?.label || k;
        const url = b.shareToken ? `${window.location.origin}/a/${b.shareToken}` : null;

        return (
          <div key={b.guildName} className="overflow-hidden rounded-3xl border border-[var(--c-line)] bg-[var(--c-surface)]">
            {/* Header carries the guild's own art, so this page and the sign up
                form feel like the same thing rather than two products. */}
            <div className="relative">
              <div
                className="h-32 bg-cover bg-center sm:h-40"
                style={
                  b.heroImageUrl
                    ? { backgroundImage: `url(${b.heroImageUrl})` }
                    : { background: "radial-gradient(120% 100% at 50% 0%, var(--c-hero-1) 0%, var(--c-hero-2) 45%, var(--c-surface) 100%)" }
                }
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-surface)] via-[color-mix(in_srgb,var(--c-surface)_60%,transparent)] to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-3 p-5">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.15em] text-amber-700 dark:text-amber-400 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
                    {b.guildName}
                  </div>
                  <h2 className="mt-0.5 text-2xl font-black text-[var(--c-ink)] drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] sm:text-3xl">
                    {b.poolName}
                  </h2>
                </div>
                <div className="rounded-full border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-3 py-1 text-xs font-semibold text-[var(--c-ink-2)] backdrop-blur">
                  {b.registered} signed up
                </div>
              </div>
            </div>

            <div className="space-y-5 p-5">
              {/* Where the guild is leaning */}
              <div className="grid gap-4 sm:grid-cols-2">
                <TugOfWar
                  title="Which side"
                  left={{ label: label("factions", "alliance"), n: b.factions.alliance || 0, color: "#4A9EFF" }}
                  right={{ label: label("factions", "horde"), n: b.factions.horde || 0, color: "#E0453E" }}
                  middle={{ label: "No preference", n: b.factions.either || 0 }}
                  unanswered={b.factions.unanswered || 0}
                />
                <TugOfWar
                  title="Realm type"
                  left={{ label: label("realmTypes", "pve"), n: b.realmTypes.pve || 0, color: "#34d399" }}
                  right={{ label: label("realmTypes", "pvp"), n: b.realmTypes.pvp || 0, color: "#fb923c" }}
                  middle={{ label: "No preference", n: b.realmTypes.either || 0 }}
                  unanswered={b.realmTypes.unanswered || 0}
                />
              </div>

              {/* The same two panels officers get. Members were already shown
                  the side and realm splits; what a player deciding what to
                  roll actually wants next is which roles and classes are thin,
                  and there is no reason that was ever officers only. */}
              {b.stats && b.registered > 0 && (
                <>
                  <RoleCoverage roles={buildRoles(b.catalog, b.stats)} total={b.registered} />
                  <ClassCoverage classes={buildClasses(b.catalog, b.stats)} total={b.registered} />
                </>
              )}

              {/* Their own answers */}
              {b.me ? (
                <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg-veil)]">
                  <div className="flex flex-wrap items-center gap-3 border-b border-[var(--c-line)] px-4 py-3">
                    <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-3)]">
                      Your answers
                    </span>
                    <span className="text-sm font-bold text-[var(--c-ink)]">{b.me.display_name}</span>
                    {b.me.faction && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{
                          color: tc(b.me.faction === "horde" ? "#E0453E" : b.me.faction === "alliance" ? "#4A9EFF" : "#94a3b8"),
                          background: `${b.me.faction === "horde" ? "#E0453E" : b.me.faction === "alliance" ? "#4A9EFF" : "#94a3b8"}1f`,
                        }}
                      >
                        {label("factions", b.me.faction)}
                      </span>
                    )}
                    {b.me.realm_type && (
                      <span className="rounded-full border border-[var(--c-line)] px-2 py-0.5 text-[11px] text-[var(--c-ink-2)]">
                        {label("realmTypes", b.me.realm_type)}
                      </span>
                    )}
                    {b.me.open_to_anything && (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-800 dark:text-amber-200">
                        Officers choose for me
                      </span>
                    )}
                    {url && (
                      <a
                        href={url}
                        className="ml-auto rounded-lg bg-[var(--c-accent)] px-3 py-1.5 text-xs font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)]"
                      >
                        Edit
                      </a>
                    )}
                  </div>

                  <ul className="divide-y divide-[var(--c-line-soft)]">
                    {b.me.combos.length === 0 && (
                      <li className="px-4 py-4 text-sm text-[var(--c-ink-3)]">
                        You did not list any classes, so your officers will pick for you.
                      </li>
                    )}
                    {b.me.combos.map((c) => {
                      const spec = specOf(c.spec_key);
                      const cls = classOf(c.class_key);
                      const role = spec?.role_key || "";
                      return (
                        <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                          <span className="w-5 text-center text-xs font-black text-[var(--c-ink-4)]">{c.rank}</span>
                          <span
                            className="h-7 w-[3px] shrink-0 rounded-full"
                            style={{ background: cls?.color || "#64748b" }}
                          />
                          <span className="leading-tight">
                            <span className="block text-sm font-bold" style={{ color: tc(cls?.color || undefined) }}>
                              {spec?.label} {cls?.label}
                            </span>
                            <span className="block text-[11px] text-[var(--c-ink-3)]">
                              {c.professions.map(profLabel).join(", ")}
                            </span>
                          </span>
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            style={{
                              color: tc(ROLE_COLOR[role] || "#64748b"),
                              background: `${ROLE_COLOR[role] || "#64748b"}1f`,
                            }}
                          >
                            {b.catalog.roles.find((r) => r.item_key === role)?.label}
                          </span>
                          {c.confirmed && (
                            <span className="ml-auto rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                              Locked in
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {b.me.note && (
                    <div className="border-t border-[var(--c-line)] px-4 py-3">
                      <div className="text-[11px] font-black uppercase tracking-[0.15em] text-[var(--c-ink-4)]">
                        Your note
                      </div>
                      <p className="mt-1 text-sm text-[var(--c-ink-2)]">{b.me.note}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 text-center">
                  <div className="text-base font-bold text-[var(--c-ink)]">You have not signed up yet</div>
                  <p className="mx-auto mt-1 max-w-sm text-sm text-[var(--c-ink-3)]">
                    Tell your officers which classes and specs you would be happy to
                    play, so they can build a raid that works.
                  </p>
                  {url ? (
                    <a
                      href={url}
                      className="mt-4 inline-block rounded-lg bg-[var(--c-accent)] px-5 py-2.5 text-sm font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)]"
                    >
                      Sign up
                    </a>
                  ) : (
                    <p className="mt-3 text-xs text-[var(--c-ink-4)]">
                      Your officers have closed the form for now.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
